import { Builder } from '@builder.io/react'
import React, { useEffect, useState } from 'react'
import defaultMobileFreshFoodImage from '../../../images/Lamb-hot-Raw.png'
import defaultDesktopFreshFoodImage from '../../../images/Shepherd-Pie-COOKED-1.png'
import defaultDesktopDryFoodImage from '../../../images/Shepherd-Pie-COOKED-2.png'
import defaultMobileDryFoodImage from '../../../images/Shepherd-Pie-COOKED-3.png'

interface DogFoodPoint {
  point: string
}

interface Image {
  imageSrc: any
  imgAltText: string
}

interface Props {
  BackgroundColor: string
  DesktopFreshFoodImage: Image
  DesktopDryFoodImage: Image
  mobileFreshFoodImage: Image
  mobileDryFoodImage: Image
  HeadingText: string
  HeadingTextColor: string
  freshDogFoodPointsIcon: Image
  FreshDogFoodPointsColor: string
  FreshDogFoodPoints: DogFoodPoint[]
  dryDogFoodPointsIcon: Image
  DryDogFoodPointsColor: string
  DryDogFoodPoints: DogFoodPoint[]
}

const ComparisonDogFood = ({
  BackgroundColor,
  DesktopFreshFoodImage,
  DesktopDryFoodImage,
  mobileFreshFoodImage,
  mobileDryFoodImage,
  HeadingText,
  HeadingTextColor,
  freshDogFoodPointsIcon,
  FreshDogFoodPointsColor,
  FreshDogFoodPoints,
  dryDogFoodPointsIcon,
  DryDogFoodPointsColor,
  DryDogFoodPoints
}: Props) => {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const mediaMatch = window.matchMedia('(min-width: 768px)')
    setMatches(mediaMatch.matches)
    mediaMatch.addEventListener('change', (e: any) => setMatches(e.matches))
    return () => mediaMatch.removeEventListener('change', (e: any) => setMatches(e.matches))
  }, [])

  const parentDivStyles = {
    container: (isDesktop: any) => ({
      backgroundColor: BackgroundColor || '#C6DABB',
      backgroundImage:
        isDesktop &&
        `url(${DesktopFreshFoodImage?.imageSrc || defaultDesktopFreshFoodImage}),url(${
          DesktopDryFoodImage?.imageSrc || defaultDesktopDryFoodImage
        })`,
      backgroundRepeat: isDesktop && 'no-repeat,no-repeat',
      backgroundPosition: isDesktop && 'left, right',
      backgroundSize: isDesktop && '54%,26%'
    })
  }

  const getPointsIcon = (pointsIcon: Image) => {
    return <img className="mx-auto h-5 w-5 md:h-8 md:w-8" src={pointsIcon?.imageSrc} alt={pointsIcon?.imgAltText} />
  }

  return (
    <div className="md:py-10" style={parentDivStyles.container(matches)}>
      {/* Heading start */}
      <h3
        className="font-serif-regular mx-auto mb-12 hidden text-center text-5xl font-medium leading-8 sm:hidden md:block"
        style={{ lineHeight: '140%', color: HeadingTextColor || '#004355', maxWidth: '840px', height: '151px' }}
      >
        {HeadingText || 'Whats the different between fresh dog food and dry dog food'}
      </h3>
      {/* Heading end */}

      <div className="flex flex-col items-center px-4 sm:flex-col sm:items-center md:flex-row md:items-start md:justify-center">
        {/* left start */}
        <div className="mb-7 sm:w-full md:mb-0 md:w-auto">
          <div className="block md:hidden">
            <img
              className="mx-auto"
              src={mobileFreshFoodImage?.imageSrc || defaultMobileFreshFoodImage}
              alt={mobileFreshFoodImage?.imgAltText}
            />
          </div>
          <div className="font-serif-regular mb-3.5 text-center text-2xl font-medium md:mb-7 md:text-3xl">
            Fresh Dog Food
          </div>
          <div className="flex flex-col items-center">
            {FreshDogFoodPoints?.map((freshDogFoodPoint: DogFoodPoint, i) => (
              <div
                className="mx-4 my-1.5 flex w-full items-center justify-start px-3.5 py-3 md:my-2.5 md:px-7 md:py-7"
                key={i}
                style={{
                  color: FreshDogFoodPointsColor || '#000000',
                  [`${matches ? 'width' : 'maxWidth'}`]: '450px',
                  backgroundColor: '#F4FEEF'
                }}
              >
                <div className="mr-3.5 md:mr-6" style={{ width: '8%' }}>
                  {getPointsIcon(freshDogFoodPointsIcon)}
                </div>
                <div className="break-words text-sm md:text-xl" style={{ width: '92%' }}>
                  {freshDogFoodPoint.point}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* left end */}

        {/* right start */}
        <div className="w-full md:w-auto">
          <div className="font-serif-regular mb-3.5 text-center text-2xl font-medium md:mb-7 md:text-3xl">
            Dry Dog Food
          </div>
          <div className="flex flex-col items-center">
            {DryDogFoodPoints?.map((dryDogFoodPoint: DogFoodPoint, i) => (
              <div
                className="mx-4 my-1.5 flex w-full items-center justify-start px-3.5 py-3 md:my-2.5 md:px-7 md:py-7"
                key={i}
                style={{
                  color: DryDogFoodPointsColor || '#000000',
                  [`${matches ? 'width' : 'maxWidth'}`]: '450px',
                  backgroundColor: '#F4FEEF'
                }}
              >
                <div className="mr-3.5 md:mr-6" style={{ width: '8%' }}>
                  {getPointsIcon(dryDogFoodPointsIcon)}
                </div>
                <div className="break-words text-sm md:text-xl" style={{ width: '92%' }}>
                  {dryDogFoodPoint.point}
                </div>
              </div>
            ))}
          </div>
          <div className="block md:hidden">
            <img
              className="mx-auto"
              src={mobileDryFoodImage?.imageSrc || defaultMobileDryFoodImage}
              alt={mobileDryFoodImage?.imgAltText}
            />
          </div>
        </div>
        {/* right end */}
      </div>
    </div>
  )
}

export default ComparisonDogFood

Builder.registerComponent(ComparisonDogFood, {
  name: 'ComparisonDogFood',
  inputs: [
    {
      name: 'BackgroundColor',
      type: 'color',
      defaultValue: '#C6DABB'
    },
    {
      name: 'DesktopFreshFoodImage',
      type: 'object',
      defaultValue: { imageSrc: null, imgAltText: '' },
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          defaultValue: null
        },
        {
          name: 'imgAltText',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'DesktopDryFoodImage',
      type: 'object',
      defaultValue: { imageSrc: null, imgAltText: '' },
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          defaultValue: null
        },
        {
          name: 'imgAltText',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'mobileFreshFoodImage',
      type: 'object',
      defaultValue: { imageSrc: null, imgAltText: '' },
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          defaultValue: null
        },
        {
          name: 'imgAltText',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'mobileDryFoodImage',
      type: 'object',
      defaultValue: { imageSrc: null, imgAltText: '' },
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          defaultValue: null
        },
        {
          name: 'imgAltText',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'HeadingText',
      type: 'text',
      defaultValue: 'Whats the different between fresh dog food and dry dog food'
    },
    {
      name: 'HeadingTextColor',
      type: 'color',
      defaultValue: '#004355'
    },
    {
      name: 'freshDogFoodPointsIcon',
      type: 'object',
      defaultValue: { imageSrc: null, imgAltText: '' },
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          defaultValue: null
        },
        {
          name: 'imgAltText',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'FreshDogFoodPointsColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'FreshDogFoodPoints',
      type: 'list',
      defaultValue: [{ point: 'Fresh, human-grade ingredients' }],
      subFields: [{ name: 'point', type: 'string', defaultValue: 'Fresh, human-grade ingredients' }]
    },
    {
      name: 'dryDogFoodPointsIcon',
      type: 'object',
      defaultValue: { imageSrc: null, imgAltText: '' },
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          defaultValue: null
        },
        {
          name: 'imgAltText',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'DryDogFoodPointsColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'DryDogFoodPoints',
      type: 'list',
      defaultValue: [{ point: 'Fresh, human-grade ingredients' }],
      subFields: [{ name: 'point', type: 'string', defaultValue: 'Fresh, human-grade ingredients' }]
    }
  ]
})
